"use client";

import React from "react";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { useQuickBooksCustomersMultiCompany } from "@/lib/hooks/api/quickbooks/quickbooks-unified";
import type { MultiCompanyResult } from "@/lib/integrations/quickbooks/quickbooks-multi-company-utils";
import type { QuickBooksCustomer } from "@/lib/types/quickbooks-unified";
import { Button } from "@/components/ui/button";

interface MatchQBCustomerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  contactId?: string; // If provided, will show existing mappings for this contact
  companyId?: string; // If provided, will update company QuickBooks mapping
  onConfirm: (
    qbCustomerId: string,
    qbCustomerName?: string,
    qbRealmId?: string,
    companyId?: string
  ) => Promise<void> | void;
}

export const MatchQBCustomerDrawer: React.FC<MatchQBCustomerDrawerProps> = React.memo(
  ({
    open,
    onOpenChange,
    title = "Select QuickBooks Customer",
    description,
    contactId,
    companyId,
    onConfirm,
  }) => {
    const [search, setSearch] = React.useState("");
    const [selectedQbId, setSelectedQbId] = React.useState("");
    const { data, isLoading } = useQuickBooksCustomersMultiCompany() as {
      data: MultiCompanyResult<QuickBooksCustomer> | undefined;
      isLoading: boolean;
    };

    const options = React.useMemo(() => {
      if (!data?.items) return [];

      // Filter customers by search term and map to options
      return data.items
        .filter(
          (item) =>
            (item.data.DisplayName?.toLowerCase().includes(search.toLowerCase()) ||
              item.data.PrimaryEmailAddr?.Address?.toLowerCase().includes(search.toLowerCase())) &&
            item.data.Id // Ensure Id is not undefined
        )
        .map((item) => ({
          value: item.data.Id!,
          label: `${item.data.DisplayName} (${item.company.companyName})`,
          description: item.data.PrimaryEmailAddr?.Address || "No email",
          // carry realmId explicitly so we don't try to parse it from description
          realmId: item.company.realmId,
        }));
    }, [data, search]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!selectedQbId) return;

      const selectedOption = options.find((opt) => opt.value === selectedQbId);
      const realmId = selectedOption?.realmId;
      void onConfirm(selectedQbId, selectedOption?.label, realmId, companyId);
    };

    return (
      <ResponsiveModal
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        description={description}
        maxWidth="lg"
        side="bottom"
        showCloseButton={true}
      >
        <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
          <ResponsiveCommandSelect
            value={selectedQbId}
            onValueChange={setSelectedQbId}
            options={options}
            placeholder="Search QuickBooks customers by name"
            searchValue={search}
            onSearchChange={setSearch}
            isSearching={isLoading}
          />
          <div className="flex flex-col gap-2 pt-4">
            <Button type="submit" disabled={!selectedQbId} className="w-full">
              Add Mapping
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </form>
      </ResponsiveModal>
    );
  }
);

MatchQBCustomerDrawer.displayName = "MatchQBCustomerDrawer";
